import { warn } from "@/lib/log";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { disablePushForThisDevice } from "@/lib/push";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // Skip /me while returning from Google OAuth; AuthCallback establishes the session first.
    if (window.location.hash?.includes("session_id=")) return;
    checkAuth();
  }, [checkAuth]);

  const setSession = useCallback((data) => {
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    await disablePushForThisDevice();
    try { await api.post("/auth/logout"); } catch (e) { warn("Logout request failed", e); }
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, setUser, setSession, logout, checkAuth }), [user, setSession, logout, checkAuth]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem("aurora_theme") || "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("aurora_theme", theme);
    window.dispatchEvent(new Event("aurora-theme"));
  }, [theme]);
  return [theme, () => setTheme((t) => (t === "dark" ? "light" : "dark"))];
}
